import { useEffect, useRef } from "react";
import { refreshOfflinePosGrant } from "../api/offlinePos";
import { useAuth } from "../context/AuthContext";
import { useConnectivity } from "../context/ConnectivityContext";
import {
  getOfflineSnapshotMetadata,
  refreshOfflinePosSnapshot,
} from "../offline/catalog";
import { syncPendingOfflineSales } from "../offline/sales";
import {
  getOfflineDevice,
  getOfflinePosSession,
  isOfflineGrantUsable,
  markOfflineDeviceServerFailure,
  saveOfflinePosSession,
} from "../offline/session";

const GRANT_REFRESH_INTERVAL_MS = 60 * 60 * 1000;
const GRANT_REFRESH_BEFORE_EXPIRY_MS = 6 * 60 * 60 * 1000;
const SNAPSHOT_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const SNAPSHOT_MAX_AGE_MS = 15 * 60 * 1000;
const OUTBOX_SYNC_INTERVAL_MS = 30 * 1000;

export default function OfflinePosRuntime() {
  const {
    user,
    selectedWorkspace,
    selectedStore,
    workspaceOperational,
    offlineAuthActive,
  } = useAuth();
  const { state } = useConnectivity();
  const grantRunningKey = useRef("");
  const snapshotRunningKey = useRef("");
  const syncRunningKey = useRef("");
  const previousConnectivityState = useRef(state);

  useEffect(() => {
    const previousState = previousConnectivityState.current;
    previousConnectivityState.current = state;
    const justCameOnline = state === "online" && previousState !== "online";
    const workspaceId = selectedWorkspace?.public_id;
    const storeId = selectedStore?.public_id;
    const userId = user?.public_id;
    if (
      state !== "online"
      || offlineAuthActive
      || !workspaceOperational
      || !workspaceId
      || !storeId
      || !userId
    ) {
      return undefined;
    }

    let cancelled = false;
    const scopeKey = `${workspaceId}:${storeId}:${userId}`;

    const recordAuthorizationFailure = async (error) => {
      const status = Number(error?.response?.status || 0);
      if (cancelled || ![403, 404, 409].includes(status)) return;
      const detail = error?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.message || error?.message || "Offline POS authorization was rejected by the server.";
      await markOfflineDeviceServerFailure({
        workspacePublicId: workspaceId,
        storePublicId: storeId,
        httpStatus: status,
        message,
      });
    };

    const refreshGrantIfNeeded = async ({ force = false } = {}) => {
      if (cancelled || grantRunningKey.current === scopeKey) return false;
      const device = await getOfflineDevice(workspaceId, storeId);
      if (!device?.device_public_id || !device?.device_secret || device.status !== "active") return false;

      if (!force) {
        const session = await getOfflinePosSession(workspaceId, storeId);
        const sameUser = session?.identity?.user?.public_id === userId;
        const expiry = new Date(session?.grant_expires_at || 0).getTime();
        const comfortablyValid = sameUser
          && isOfflineGrantUsable(session)
          && Number.isFinite(expiry)
          && expiry - Date.now() > GRANT_REFRESH_BEFORE_EXPIRY_MS;
        if (comfortablyValid) return false;
      }

      grantRunningKey.current = scopeKey;
      try {
        const response = await refreshOfflinePosGrant(
          device.device_public_id,
          device.device_secret,
        );
        if (cancelled) return false;
        const data = response.data;
        await saveOfflinePosSession({
          workspacePublicId: workspaceId,
          storePublicId: storeId,
          devicePublicId: data.device.public_id,
          grant: data.grant,
          grantExpiresAt: data.grant_expires_at,
          identity: data.identity,
        });
        return true;
      } catch (error) {
        await recordAuthorizationFailure(error);
        return false;
      } finally {
        if (grantRunningKey.current === scopeKey) grantRunningKey.current = "";
      }
    };

    const syncPendingIfNeeded = async () => {
      if (cancelled || syncRunningKey.current === scopeKey) {
        return { attempted: 0, acknowledged: 0, results: [] };
      }
      syncRunningKey.current = scopeKey;
      try {
        return await syncPendingOfflineSales({
          workspacePublicId: workspaceId,
          storePublicId: storeId,
        });
      } catch (error) {
        // The outbox restores SYNCING rows to PENDING internally. Never discard a
        // locally completed sale because reconnect/auth/provider work failed.
        await recordAuthorizationFailure(error);
        return { attempted: 0, acknowledged: 0, results: [] };
      } finally {
        if (syncRunningKey.current === scopeKey) syncRunningKey.current = "";
      }
    };

    const refreshSnapshotIfNeeded = async ({ force = false } = {}) => {
      if (cancelled || snapshotRunningKey.current === scopeKey) return;
      const [device, session, metadata] = await Promise.all([
        getOfflineDevice(workspaceId, storeId),
        getOfflinePosSession(workspaceId, storeId),
        getOfflineSnapshotMetadata(workspaceId, storeId),
      ]);
      if (
        !device?.device_public_id
        || device.status !== "active"
        || !session
        || session.device_public_id !== device.device_public_id
        || session.identity?.user?.public_id !== userId
        || !isOfflineGrantUsable(session)
      ) {
        return;
      }

      const cachedAt = new Date(metadata?.cached_at || 0).getTime();
      const fresh = Number.isFinite(cachedAt) && Date.now() - cachedAt < SNAPSHOT_MAX_AGE_MS;
      if (!force && fresh) return;

      snapshotRunningKey.current = scopeKey;
      try {
        await refreshOfflinePosSnapshot(workspaceId, storeId);
      } catch (error) {
        await recordAuthorizationFailure(error);
      } finally {
        if (snapshotRunningKey.current === scopeKey) snapshotRunningKey.current = "";
      }
    };

    const keepReady = async () => {
      await refreshGrantIfNeeded({ force: justCameOnline });
      if (cancelled) return;
      const syncResult = await syncPendingIfNeeded();
      if (cancelled) return;
      await refreshSnapshotIfNeeded({
        force: justCameOnline || Number(syncResult?.acknowledged || 0) > 0,
      });
    };

    keepReady().catch(() => {});
    const grantTimer = window.setInterval(() => {
      refreshGrantIfNeeded().catch(() => {});
    }, GRANT_REFRESH_INTERVAL_MS);
    const syncTimer = window.setInterval(() => {
      syncPendingIfNeeded()
        .then((result) => {
          if (Number(result?.acknowledged || 0) > 0) {
            return refreshSnapshotIfNeeded({ force: true });
          }
          return undefined;
        })
        .catch(() => {});
    }, OUTBOX_SYNC_INTERVAL_MS);
    const snapshotTimer = window.setInterval(() => {
      refreshSnapshotIfNeeded().catch(() => {});
    }, SNAPSHOT_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(grantTimer);
      window.clearInterval(syncTimer);
      window.clearInterval(snapshotTimer);
      if (grantRunningKey.current === scopeKey) grantRunningKey.current = "";
      if (snapshotRunningKey.current === scopeKey) snapshotRunningKey.current = "";
      if (syncRunningKey.current === scopeKey) syncRunningKey.current = "";
    };
  }, [
    offlineAuthActive,
    selectedStore?.public_id,
    selectedWorkspace?.public_id,
    state,
    user?.public_id,
    workspaceOperational,
  ]);

  return null;
}
